import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AllCommunityModule,
  CellSelectionOptions,
  ColDef,
  GetRowIdParams,
  ModuleRegistry,
  RowSelectionOptions,
  Theme,
  themeQuartz
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useSearchParams } from "react-router-dom";
import { Stack, LinearProgress, Typography } from "@mui/material";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import {
  Application,
  getAdminApplicationListRequest,
  getAdminApplicationListResponse
} from "../../Graphql/types/application";
import ViewAppDetailButton from "./ViewAppDetailButton";
import { adminGetApplicationListQuery } from "../../Graphql/querries/applicationQuery";
import WithAuth from "../../Hoc/WithAuth";
import SearchArea from "../Common/Search/SearchArea";
import { ApplicationSearchConfig } from "./config/applicationSearchConfig";

ModuleRegistry.registerModules([AllCommunityModule]);

const ApplicationList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const [rowData, setRowData] = useState<Application[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>();
  const [searchParams] = useSearchParams();

  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const myTheme = themeQuartz.withParams({
    // color and style of border around selection
    rangeSelectionBorderColor: "rgb(193, 0, 97)",
    rangeSelectionBorderStyle: "dashed",
    // background color of selection - you can use a semi-transparent color
    // and it wil overlay on top of the existing cells
    rangeSelectionBackgroundColor: "rgb(255, 0, 128, 0.1)",
    // color used to indicate that data has been copied form the cell range
    rangeSelectionHighlightColor: "rgb(60, 188, 0, 0.3)"
  });

  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const { mutate, isPending } = useMutation<
    getAdminApplicationListResponse,
    Error,
    getAdminApplicationListRequest
  >({
    mutationKey: ["getApplicationListr"],
    mutationFn: (input) =>
      fetchGraphQL(adminGetApplicationListQuery, { input }, setAlert),
    onSuccess: (response) => {
      setRowData(response.adminGetApplicationList || []);
    },
    onError: () => {
      setRowData([]);
    }
  });

  const rowSelection: RowSelectionOptions = {
    mode: "singleRow",
    checkboxes: false
  };

  const cellSelection = useMemo<boolean | CellSelectionOptions>(() => {
    return { handle: { mode: "range" } };
  }, []);

  useEffect(() => {
    setColumnDefs([
      { field: "jobInfo.jobTitle", minWidth: 150, headerName: "Job title" },
      { field: "id", headerName: "Application Id" },
      { field: "jobInfo.totalPay", headerName: "Total Pay" },
      { field: "submittedDate", headerName: "Submitted date" },
      { field: "status" },
      {
        field: "actions",
        headerName: "Actions",
        cellRenderer: ViewAppDetailButton
      }
    ]);
  }, []);

  useEffect(() => {
    const queryParams: getAdminApplicationListRequest = {
      memberId: searchParams.get("memberId") ?? undefined,
      jobId: searchParams.get("jobId") ?? undefined,
      email: searchParams.get("email") ?? undefined,
      phone: searchParams.get("phone") ?? undefined
    };

    mutate({ ...queryParams });
  }, [searchParams]);

  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.id),
    []
  );

  const defaultColDef = useMemo(() => {
    return {
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      resizable: true
    };
  }, []);

  return (
    <div style={containerStyle}>
      <SearchArea formConfig={ApplicationSearchConfig} />
      {isPending && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Application Listing
        </Typography>
      </Stack>
      <div style={gridStyle}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
          theme={theme}
          cellSelection={cellSelection}
          getRowId={getRowId}
        />
      </div>
    </div>
  );
};

export default WithAuth(ApplicationList);
